import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, Check, Folder, FolderPlus, Layers } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { listEsteiraGroups, getEsteiraFormOptions } from '@/services/api/esteira';
import { getFolderTree, createFolder } from '@/services/api/template-library';
import { listBuilderItems, deleteBuilderItem, batchSubmitBuilderItems } from '@/services/api/template-builder';
import type { BuilderItem } from '@/types/template-builder';
import BuilderWorkspace from './components/BuilderWorkspace';
import PageSelectDialog from './components/PageSelectDialog';

const STEPS = ['Esteira', 'Pasta', 'Montar'];

function groupCategories(g: any): string[] {
  const set = new Set<string>();
  for (const p of g?.pages_meta ?? []) if (p.internal_category_name) set.add(p.internal_category_name);
  return [...set];
}

function dedupePages(g: any): any[] {
  const seen = new Set<string>();
  const out: any[] = [];
  for (const p of g?.pages_meta ?? []) {
    const fb = p.fb_page_id ?? '';
    if (!fb || p.is_usable === false || seen.has(fb)) continue;
    seen.add(fb);
    out.push(p);
  }
  return out;
}

// Busca TODAS as esteiras ATIVAS paginando (backend limita per_page; 50 é aceito).
async function fetchAllEsteiras(): Promise<any[]> {
  const perPage = 50;
  const first = await listEsteiraGroups({ status: 'active', per_page: perPage, page: 1 });
  let items = ((first as any).items ?? []) as any[];
  const totalPages = (first as any).pages ?? 1;
  for (let p = 2; p <= totalPages; p++) {
    const r = await listEsteiraGroups({ status: 'active', per_page: perPage, page: p });
    items = items.concat(((r as any).items ?? []) as any[]);
  }
  return items;
}

function Stepper({ step, onStepClick }: { step: number; onStepClick?: (n: number) => void }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const done = n < step;
        const active = n === step;
        const clickable = done && !!onStepClick;
        return (
          <div key={label} className="flex items-center gap-2">
            <button type="button" disabled={!clickable} onClick={() => clickable && onStepClick!(n)} className={`flex items-center gap-2 ${clickable ? 'cursor-pointer' : 'cursor-default'}`}>
              <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${done ? 'bg-emerald-500 text-white' : active ? 'bg-indigo-600 text-white' : 'bg-muted text-muted-foreground'}`}>
                {done ? <Check className="h-4 w-4" /> : n}
              </div>
              <span className={`text-sm ${active ? 'font-semibold text-slate-900' : done ? 'text-slate-700 hover:text-slate-900' : 'text-muted-foreground'}`}>{label}</span>
            </button>
            {n < STEPS.length && <div className="mx-1 h-px w-8 bg-border" />}
          </div>
        );
      })}
    </div>
  );
}

export default function BuilderWizardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  // preset vindo do atalho "Nova peça aqui" (mesma esteira+pasta) → pula pro Montar.
  const preset = (location.state ?? null) as { esteiraGroupId?: number; folderId?: number | null; projectId?: number | null } | null;
  const [step, setStep] = useState(preset?.esteiraGroupId ? 3 : 1);

  // seleção
  const [esteiraSearch, setEsteiraSearch] = useState('');
  const [groupId, setGroupId] = useState<number | null>(preset?.esteiraGroupId ?? null);
  // Atalho "Nova peça aqui": herda o PROJETO da peça existente (não o da esteira,
  // que pode ser None enquanto a pasta é de um projeto → mismatch pasta×projeto no
  // create → 403). Ver messageria-template-builder.
  const [projectId, setProjectId] = useState<number | null>(preset?.projectId ?? null);
  const [folderId, setFolderId] = useState<number | null>(preset?.folderId ?? null);
  const [presetDone, setPresetDone] = useState(false);

  const [newFolderName, setNewFolderName] = useState('');
  const [folderSearch, setFolderSearch] = useState('');

  // ── dados ──
  const { data: formOpts } = useQuery({ queryKey: ['builder', 'form-opts'], queryFn: getEsteiraFormOptions });
  const projects = formOpts?.projects ?? [];

  const { data: groupsResp, isLoading: loadingEsteiras } = useQuery({
    queryKey: ['builder', 'esteiras'],
    queryFn: fetchAllEsteiras,
  });
  const groups = (groupsResp ?? []) as any[];
  // Preset: quando as esteiras carregam, resolve o projeto da esteira escolhida.
  useEffect(() => {
    // fallback: só resolve o projeto pela esteira se o atalho NÃO trouxe projectId
    // (navegação antiga). Quando veio no state (mesmo null), respeita o da peça.
    if (!presetDone && preset?.esteiraGroupId && preset.projectId === undefined && groups.length) {
      const g = groups.find((x) => x.id === preset.esteiraGroupId);
      if (g) { setProjectId(g.project_id ?? null); setPresetDone(true); }
    }
  }, [groups, presetDone, preset]);
  const filteredGroups = useMemo(() => {
    const q = esteiraSearch.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => String(g.name ?? '').toLowerCase().includes(q));
  }, [groups, esteiraSearch]);

  const { data: folderTree, refetch: refetchFolders } = useQuery({
    queryKey: ['builder', 'folders', projectId],
    queryFn: () => getFolderTree(projectId),
    enabled: step >= 2,
  });
  const folders = folderTree?.folders ?? [];
  const rootCount = folderTree?.root_template_count ?? 0;
  const visibleFolders = useMemo(() => {
    const q = folderSearch.trim().toLowerCase();
    if (!q) return folders;
    return folders.filter((f) => String(f.name ?? '').toLowerCase().includes(q));
  }, [folders, folderSearch]);

  // ── ações ──
  const pickEsteira = (g: any) => {
    setGroupId(g.id);
    setProjectId(g.project_id ?? null);
  };

  const createFolderMut = useMutation({
    mutationFn: () => createFolder({ name: newFolderName.trim(), project_id: projectId, parent_id: null }),
    onSuccess: () => {
      setNewFolderName('');
      toast({ title: 'Pasta criada' });
      refetchFolders();
    },
    onError: (e: any) => toast({ title: 'Erro ao criar pasta', description: String(e?.message ?? e), variant: 'destructive' }),
  });

  // contexto pro workspace (passo 3)
  const selectedGroup = groups.find((g) => g.id === groupId);
  const esteiraPages = useMemo(() => dedupePages(selectedGroup), [selectedGroup]);
  const folderName = folderId == null ? 'Raiz' : (folders.find((f) => f.id === folderId)?.name ?? 'pasta');

  // ── Fila (criar vários e enviar de uma vez, sem sair da tela) ──
  const [formKey, setFormKey] = useState(0);            // re-monta o workspace = form limpo
  const [editingItem, setEditingItem] = useState<BuilderItem | null>(null);
  const [queuePageOpen, setQueuePageOpen] = useState(false);
  const { data: allItemsResp, refetch: refetchQueue } = useQuery({
    queryKey: ['builder', 'queue-items', projectId],
    queryFn: () => listBuilderItems(projectId ?? undefined),
    enabled: step === 3 && !!groupId,
    refetchOnMount: 'always',
  });
  const queueDrafts = useMemo(
    () => (allItemsResp?.items ?? []).filter((it) => it.esteira_group_id === groupId && (it.folder_id ?? null) === (folderId ?? null) && it.state === 'draft'),
    [allItemsResp, groupId, folderId],
  );
  const removeMut = useMutation({
    mutationFn: (id: number) => deleteBuilderItem(id),
    onSuccess: (_r, id) => { refetchQueue(); if (editingItem?.id === id) { setEditingItem(null); setFormKey((k) => k + 1); } },
  });
  const batchMut = useMutation({
    mutationFn: ({ ids, pageId }: { ids: number[]; pageId: string }) => batchSubmitBuilderItems(ids, pageId),
    onSuccess: (r) => { toast({ title: `${r.submitted}/${r.total} enviadas à Meta`, description: 'Acompanhe o status em Suas peças.' }); navigate('/meta/template-builder'); },
    onError: (e: any) => toast({ title: 'Falha no envio em lote', description: String(e?.message ?? e), variant: 'destructive' }),
  });
  const handleQueued = () => { setEditingItem(null); setFormKey((k) => k + 1); refetchQueue(); };

  return (
    <div className="px-6 pb-6 pt-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <button onClick={() => navigate('/meta/template-builder')} className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" /> Template Builder
        </button>
        <Stepper step={step} onStepClick={(n) => setStep(n)} />
      </div>

      {/* ─── STEP 1: Esteira ─── */}
      {step === 1 && (
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-bold tracking-tight text-[#161c2b]">Escolha a esteira</h2>
            <p className="text-sm text-[#6b7285]">A página do disparo você escolhe depois, ao submeter ou testar.</p>
          </div>
          <Input
            value={esteiraSearch}
            onChange={(e) => setEsteiraSearch(e.target.value)}
            placeholder="Buscar esteira pelo nome…"
            className="max-w-sm"
          />

          {loadingEsteiras ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-[92px] animate-pulse rounded-xl border bg-slate-100" />
              ))}
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="rounded-xl border border-dashed p-10 text-center text-sm text-muted-foreground">Nenhuma esteira ativa encontrada.</div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredGroups.map((g) => {
                const active = groupId === g.id;
                const cats = groupCategories(g);
                return (
                  <button
                    key={g.id}
                    onClick={() => pickEsteira(g)}
                    className={`rounded-xl border p-4 text-left transition ${active ? 'border-indigo-500 bg-indigo-50/40 ring-2 ring-indigo-200' : 'hover:border-slate-300 hover:shadow-sm'}`}
                  >
                    <div className="mb-1.5 flex items-center gap-2">
                      <Layers className="h-4 w-4 shrink-0 text-indigo-500" />
                      <span className="truncate font-medium">{g.name}</span>
                      {active && <Check className="ml-auto h-4 w-4 shrink-0 text-indigo-600" />}
                    </div>
                    <div className="mb-2 text-xs text-muted-foreground">{(g.page_ids?.length ?? 0)} páginas</div>
                    {cats.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {cats.slice(0, 4).map((c) => (
                          <span key={c} className="truncate rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] font-medium text-indigo-600 ring-1 ring-inset ring-indigo-100">{c}</span>
                        ))}
                        {cats.length > 4 && <span className="text-[11px] text-muted-foreground">+{cats.length - 4}</span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* rodapé sticky — sempre visível */}
          <div className="sticky bottom-0 z-10 -mx-6 border-t bg-background/95 px-6 py-3 backdrop-blur">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm">
                {groupId ? (
                  <span><span className="text-muted-foreground">Esteira selecionada: </span><span className="font-semibold">{groups.find((g) => g.id === groupId)?.name}</span></span>
                ) : (
                  <span className="text-muted-foreground">Selecione uma esteira acima</span>
                )}
              </div>
              <button
                disabled={!groupId}
                onClick={() => setStep(2)}
                style={{ border: 'none', borderRadius: 11, padding: '12px 22px', fontSize: 13.5, fontWeight: 700, color: '#fff', background: groupId ? '#6a5cf0' : '#e8eaf0', cursor: groupId ? 'pointer' : 'not-allowed' }}
              >
                Continuar →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── STEP 2: Pasta ─── */}
      {step === 2 && (
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-bold tracking-tight text-[#161c2b]">Escolha a pasta</h2>
            <p className="text-sm text-[#6b7285]">Esteira: {selectedGroup?.name ?? '—'}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Projeto:</span>
            <select
              className="h-9 rounded-md border bg-background px-2 text-sm"
              value={projectId ?? ''}
              onChange={(e) => { setProjectId(e.target.value === '' ? null : Number(e.target.value)); setFolderId(null); }}
            >
              <option value="">Pessoal (sem projeto)</option>
              {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
            <Input value={folderSearch} onChange={(e) => setFolderSearch(e.target.value)} placeholder="Buscar pasta…" className="h-9 max-w-xs" />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {!folderSearch.trim() && (
              <button
                onClick={() => setFolderId(null)}
                className={`flex items-center gap-3 rounded-xl border p-4 text-left transition ${folderId === null ? 'border-indigo-500 ring-2 ring-indigo-200' : 'hover:border-slate-300 hover:shadow-sm'}`}
              >
                <Folder className="h-5 w-5 text-slate-400" />
                <div><div className="font-medium">Raiz</div><div className="text-xs text-muted-foreground">{rootCount} templates</div></div>
              </button>
            )}
            {visibleFolders.map((f) => (
              <button
                key={f.id}
                onClick={() => setFolderId(f.id)}
                className={`flex items-center gap-3 rounded-xl border p-4 text-left transition ${folderId === f.id ? 'border-indigo-500 ring-2 ring-indigo-200' : 'hover:border-slate-300 hover:shadow-sm'}`}
              >
                <Folder className="h-5 w-5 shrink-0 text-indigo-500" />
                <div className="min-w-0"><div className="truncate font-medium">{f.name}</div><div className="text-xs text-muted-foreground">{f.template_count} templates</div></div>
                {folderId === f.id && <Check className="ml-auto h-4 w-4 shrink-0 text-indigo-600" />}
              </button>
            ))}
            {folderSearch.trim() && visibleFolders.length === 0 && (
              <div className="col-span-full rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">Nenhuma pasta com “{folderSearch}”.</div>
            )}
          </div>

          <div className="flex items-center gap-2 rounded-xl border border-dashed p-3">
            <FolderPlus className="h-4 w-4 text-muted-foreground" />
            <Input
              value={newFolderName}
              placeholder="Nova pasta…"
              className="h-8 max-w-xs"
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && newFolderName.trim()) createFolderMut.mutate(); }}
            />
            <Button size="sm" variant="outline" disabled={!newFolderName.trim() || createFolderMut.isPending} onClick={() => createFolderMut.mutate()}>Criar</Button>
          </div>

          <div className="sticky bottom-0 z-10 -mx-6 border-t bg-background/95 px-6 py-3 backdrop-blur">
            <div className="flex items-center justify-between gap-3">
              <button onClick={() => setStep(1)} className="text-sm font-semibold text-[#4b5263] hover:text-slate-900">← Voltar</button>
              <div className="flex items-center gap-4">
                <span className="text-sm text-[#6b7285]">{folderName}</span>
                <button onClick={() => setStep(3)} style={{ border: 'none', borderRadius: 11, padding: '12px 22px', fontSize: 13.5, fontWeight: 700, color: '#fff', background: '#6a5cf0', cursor: 'pointer' }}>Continuar →</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── STEP 3: Montar + fila (form 2 colunas + preview estreito = cabe sem scroll de página; só a fila rola quando enche) ─── */}
      {step === 3 && groupId && (
        <div>
          <div className="flex flex-col gap-3">
            <div>
                <BuilderWorkspace
                  key={formKey}
                  esteiraGroupId={groupId}
                  esteiraName={selectedGroup?.name}
                  folderName={folderName}
                  folderId={folderId}
                  projectId={projectId}
                  pages={esteiraPages}
                  existingItem={editingItem}
                  onQueued={handleQueued}
                  hasQueue={queueDrafts.length > 0}
                  onSaved={() => navigate('/meta/template-builder')}
                />
              </div>
              {/* Barra da fila — só aparece quando há ≥1 na fila; fica colada no rodapé */}
              {queueDrafts.length > 0 && (
              <div className="sticky bottom-3 z-10 rounded-2xl border bg-white p-3 shadow-lg">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-900">Fila de aprovação ({queueDrafts.length})</span>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setEditingItem(null); setFormKey((k) => k + 1); }}>+ Montar outra</Button>
                    <Button size="sm" disabled={batchMut.isPending} onClick={() => setQueuePageOpen(true)} className="bg-indigo-600 hover:bg-indigo-700">
                      Enviar todas à Meta ({queueDrafts.length})
                    </Button>
                  </div>
                </div>
                <div className="max-h-44 space-y-2 overflow-y-auto pr-1">
                    {queueDrafts.map((it) => (
                      <div key={it.id} className={`flex items-center gap-3 rounded-xl border p-2 ${editingItem?.id === it.id ? 'border-indigo-400 bg-indigo-50' : 'bg-white'}`}>
                        {it.image_url ? (
                          <img src={it.image_url} alt="" className="h-11 w-11 shrink-0 rounded-lg object-cover" />
                        ) : (
                          <div className="h-11 w-11 shrink-0 rounded-lg bg-slate-100" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-semibold">{it.title || it.name || `peca-${it.id}`}</span>
                            {editingItem?.id === it.id && <span className="shrink-0 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">editando</span>}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">{it.text_content?.trim() || 'sem texto'}</div>
                          {it.buttons && it.buttons.length > 0 && it.buttons[0].title && (
                            <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">▷ {it.buttons[0].title}</span>
                          )}
                        </div>
                        <button onClick={() => { setEditingItem(it); setFormKey((k) => k + 1); }} title="Editar" className="shrink-0 rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-indigo-600">✎</button>
                        <button onClick={() => removeMut.mutate(it.id)} disabled={removeMut.isPending} title="Remover" className="shrink-0 rounded-md px-2 py-1 text-slate-400 hover:bg-red-50 hover:text-red-600">✕</button>
                      </div>
                    ))}
                </div>
              </div>
              )}
            </div>

          <PageSelectDialog
            open={queuePageOpen}
            onOpenChange={setQueuePageOpen}
            esteiraName={selectedGroup?.name}
            pages={esteiraPages}
            onSelect={(fb) => { if (fb && queueDrafts.length) batchMut.mutate({ ids: queueDrafts.map((d) => d.id), pageId: fb }); }}
          />
        </div>
      )}
    </div>
  );
}
